import { Badge, Box, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ExtensionDurationUnit, type OptionDto } from "../../../autogen/bff-api";
import { isDisplayableDate } from "../../../common/formatting/displayDate";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useExtensionName } from "./extensionName";
import { extensionStatus, type ExtensionStatus } from "./extensionStatus";

export const OptionPeriod = ({ option }: { option: OptionDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const [startDate, endDate, deadlineDate] = [option.startDate, option.endDate, option.deadline].map((date) =>
    date && isDisplayableDate(date) ? date : undefined
  );
  const period =
    startDate && endDate
      ? `${displayDate(startDate)} – ${displayDate(endDate)}`
      : [
          startDate && `${t("Start date")}: ${displayDate(startDate)}`,
          endDate && `${t("End date")}: ${displayDate(endDate)}`,
        ]
          .filter(Boolean)
          .join(" · ");
  const deadline = deadlineDate && t("deadline {{date}}", { date: displayDate(deadlineDate) });
  const text = [period, deadline].filter(Boolean).join(" · ");
  if (!text) return null;
  return (
    <Text fontSize="sm" data-testid="option-period">
      {text}
    </Text>
  );
};

export const OptionDuration = ({ option }: { option: OptionDto }) => {
  const { t } = useTranslation();
  const extension = option.extension;
  if (!extension || option.datesSetByHand) return null;
  const unit =
    extension.unit === ExtensionDurationUnit.Years
      ? t("year", { count: extension.duration })
      : t("month", { count: extension.duration });
  return (
    <Text fontSize="sm" color="smTextSecondary">
      {t("{{duration}} {{unit}}, deadline {{months}} {{monthUnit}} before the contract ends", {
        duration: extension.duration,
        unit,
        months: extension.exerciseDeadlineMonths,
        monthUnit: t("month", { count: extension.exerciseDeadlineMonths }),
      })}
    </Text>
  );
};

const STATUS_COLORS: Record<ExtensionStatus["kind"], { bg: string; color: string }> = {
  exercised: { bg: "smChipGreenBg", color: "smChipGreenFg" },
  declined: { bg: "smStatusNeutralBg", color: "smStatusNeutralText" },
  lapsed: { bg: "smStatusNeutralBg", color: "smStatusNeutralText" },
  deadlinePassed: { bg: "smChipRedBg", color: "smChipRedFg" },
  deadlineSoon: { bg: "smChipAmberBg", color: "smChipAmberFg" },
  undecided: { bg: "smStatusNeutralBg", color: "smStatusNeutralText" },
};

export const useOptionStatusText = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  return useCallback(
    (option: OptionDto, today: Date = new Date()): string => {
      const status = extensionStatus(option, today);
      switch (status.kind) {
        case "exercised":
          return status.at && status.by
            ? t("Exercised {{date}} by {{name}}", { date: displayDate(status.at), name: status.by })
            : t("Executed");
        case "declined":
          return status.at && status.by
            ? t("Will not be exercised – decided {{date}} by {{name}}", {
                date: displayDate(status.at),
                name: status.by,
              })
            : t("Will not be exercised");
        case "lapsed":
          return t("Lapsed");
        case "deadlinePassed":
          return t("Deadline passed");
        case "deadlineSoon":
          return status.days === 0 ? t("Deadline today") : t("Deadline in {{count}} days", { count: status.days });
        case "undecided":
          return t("Not decided");
      }
    },
    [t, displayDate]
  );
};

export const OptionStatus = ({ option, today = new Date() }: { option: OptionDto; today?: Date }) => {
  const statusText = useOptionStatusText();
  const { bg, color } = STATUS_COLORS[extensionStatus(option, today).kind];
  return (
    <Badge bg={bg} color={color} textTransform="none" fontWeight="semibold" px="2" py="0.5" rounded="md" data-testid="option-status">
      {statusText(option, today)}
    </Badge>
  );
};

export const OptionDeadlinePassed = ({ option, today = new Date() }: { option: OptionDto; today?: Date }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  if (!option.deadline || extensionStatus(option, today).kind !== "deadlinePassed") return null;
  return (
    <Box
      mt="2"
      px="2"
      py="1"
      borderWidth="1px"
      borderColor="smWarningSoftBorder"
      bg="smWarningSoft"
      rounded="md"
      data-testid="option-deadline-passed"
    >
      <Text fontSize="sm">{t("The deadline to exercise passed on {{date}}", { date: displayDate(option.deadline) })}</Text>
    </Box>
  );
};

export const OptionLapsedBecause = ({ option, options }: { option: OptionDto; options: OptionDto[] }) => {
  const { t } = useTranslation();
  const nameOf = useExtensionName(options);
  const declined = options.find((other) => other.id === option.lapsedBecauseOfOptionId);
  if (!option.isLapsed || !declined) return null;
  return (
    <Text fontSize="sm" color="smMuted" pt="1" data-testid="option-lapsed-because">
      {t("Lapsed because {{name}} was not exercised.", { name: nameOf(declined) })}
    </Text>
  );
};
